/* ═══════════════════════════════════════════
   CSV IMPORT UTILITIES
   Multi-platform CSV parsing + taxonomy mapping
   Supports: Goodreads, StoryGraph, LibraryThing,
   Kindle, BookBub, Literal.club, Oku
═══════════════════════════════════════════ */

import { BOOK_TAXONOMY } from './bookTaxonomy';
import { normalizeTitle, normalizeAuthor, titleSimilarity } from './duplicateDetection';

/* ─────────────────────────────────────────
   SHARED HELPERS
───────────────────────────────────────── */
function pagesToLength(pages) {
  if (!pages) return null;
  if (pages < 150) return 'Novella (1-149 pages)';
  if (pages < 250) return 'Short (150-249)';
  if (pages < 400) return 'Medium (250-399)';
  if (pages < 600) return 'Long (400-599)';
  return 'Epic (600+)';
}

function mapReadStatus(raw) {
  if (!raw) return null;
  const s = raw.toLowerCase().trim();
  if (['read', 'finished'].includes(s)) return 'read';
  if (['currently-reading', 'currently reading', 'reading', 'started'].includes(s)) return 'reading';
  if (['to-read', 'to read', 'want-to-read', 'want to read', 'tbr'].includes(s)) return 'to-read';
  return null;
}

export const PLATFORM_INFO = {
  goodreads: {
    name: 'Goodreads',
    exportSteps: [
      'Go to My Books on Goodreads',
      'Click "Import and Export" in the left sidebar (under Tools)',
      'Click "Export Library"',
      'Download the CSV file and upload it here',
    ],
  },
  storygraph: {
    name: 'StoryGraph',
    exportSteps: [
      'Go to your StoryGraph profile',
      'Click "Manage Account" → "Export Your Data"',
      'Click "Generate Export" and wait for the download',
      'Upload the CSV file here',
    ],
  },
  librarything: {
    name: 'LibraryThing',
    exportSteps: [
      'Go to More → Import/Export on LibraryThing',
      'Click "Export as CSV" under Export',
      'Download the file and upload it here',
    ],
  },
  kindle: {
    name: 'Kindle',
    exportSteps: [
      'Go to amazon.com/hz/mycd/digital-console/contentlist',
      'Select "Books" and "All" in the filters',
      'Click the three dots (⋮) → "Export" for your library',
      'If no export option, use the Goodreads sync instead',
    ],
  },
  bookbub: {
    name: 'BookBub',
    exportSteps: [
      'Go to bookbub.com and sign in',
      'Click your profile → "My Books"',
      'Look for "Export" or "Download" option',
      'Upload the CSV file here',
    ],
  },
  literal: {
    name: 'Literal.club',
    exportSteps: [
      'Go to literal.club and sign in',
      'Go to Settings → "Export Library"',
      'Download the CSV and upload it here',
    ],
  },
  oku: {
    name: 'Oku',
    exportSteps: [
      'Go to oku.club and sign in',
      'Go to Settings → "Export Data"',
      'Download the CSV and upload it here',
    ],
  },
};

/* ─────────────────────────────────────────
   GOODREADS SHELF → TAXONOMY MAPPING
───────────────────────────────────────── */
const GOODREADS_GENRE_MAP = {
  'fantasy': 'High fantasy', 'high-fantasy': 'High fantasy', 'epic-fantasy': 'High fantasy',
  'urban-fantasy': 'Urban fantasy', 'steampunk': 'Steampunk',
  'paranormal': 'Magic', 'paranormal-romance': 'Magic', 'magic': 'Magic',
  'time-travel': 'Time travel', 'angels': 'Angels', 'demons': 'Demons',
  'fae': 'Fae', 'faeries': 'Fae', 'fairy-tales': 'Fae', 'faerie': 'Fae',
  'monsters': 'Monsters', 'orcs': 'Orcs',
  'werewolves': 'Shapeshifters (werewolves)', 'shapeshifters': 'Shapeshifters (werewolves)',
  'dragons': 'Dragon shifter', 'vampires': 'Vampires', 'witches': 'Witches',
  'sci-fi': 'Science fiction / aliens', 'science-fiction': 'Science fiction / aliens',
  'dark-romance': 'Dark romance', 'dark': 'Dark romance',
  'ya': 'Young adult', 'young-adult': 'Young adult',
  'new-adult': 'New adult', 'na': 'New adult',
  'mystery': 'Mystery', 'suspense': 'Suspense', 'thriller': 'Suspense',
  'dystopian': 'Dystopian', 'omegaverse': 'Omegaverse',
  'humor': 'Funny', 'humorous': 'Funny', 'funny': 'Funny',
  'horror': 'Horror', 'gothic': 'Horror',
};

const GOODREADS_TROPE_MAP = {
  'enemies-to-lovers': 'Enemies to lovers', 'slow-burn': 'Slow burn',
  'fake-dating': 'Fake relationship', 'fake-relationship': 'Fake relationship',
  'friends-to-lovers': 'Friends to lovers', 'forced-proximity': 'Forced proximity',
  'arranged-marriage': 'Arranged marriage', 'love-triangle': 'Love triangle',
  'second-chance': 'Second chances', 'second-chances': 'Second chances',
  'fated-mates': 'Fated mates', 'grumpy-sunshine': 'Grumpy & sunshine',
  'age-gap': 'Age gap', 'found-family': 'Found family',
  'friends-with-benefits': 'Friends with benefits',
  'hurt-comfort': 'Hurt/comfort', 'insta-love': 'Insta-love',
  'forbidden-love': 'Forced proximity', 'marriage-of-convenience': 'Marriage of convenience',
  'reverse-harem': 'Reverse harem',
};

/* ─────────────────────────────────────────
   STORYGRAPH → TAXONOMY MAPPING
───────────────────────────────────────── */
const STORYGRAPH_GENRE_MAP = {
  ...GOODREADS_GENRE_MAP,
  'romance': null, // too generic to map
  'fantasy romance': 'High fantasy',
  'paranormal romance': 'Magic',
  'dark romance': 'Dark romance',
  'contemporary romance': null,
  'historical romance': null,
  'science fiction': 'Science fiction / aliens',
};

const STORYGRAPH_MOOD_MAP = {
  'dark': 'Dark romance',
  'emotional': 'Angst',
  'funny': 'Funny',
  'hopeful': null,
  'inspiring': null,
  'lighthearted': 'Funny',
  'mysterious': 'Mystery',
  'sad': 'Angst',
  'tense': 'Suspense',
  'adventurous': null,
  'challenging': null,
  'reflective': null,
};

const STORYGRAPH_MOOD_TO_ELEMENTS = {
  'dark': null,
  'emotional': null,
  'adventurous': 'Survival',
  'tense': null,
};

const STORYGRAPH_CW_MAP = {
  'abuse': 'Third party abuse',
  'sexual abuse': 'Third party abuse',
  'domestic abuse': 'Abuse between MCs',
  'animal death': 'Animal death',
  'animal cruelty': 'Animal death',
  'dubious consent': 'Dubious consent',
  'dubcon': 'Dubious consent',
  'violence': 'Graphic violence',
  'graphic violence': 'Graphic violence',
  'mental illness': 'Mental illness / mental trauma',
  'mental health': 'Mental illness / mental trauma',
  'self harm': 'Self harm',
  'self-harm': 'Self harm',
  'suicide': 'Suicide/ideation',
  'suicidal ideation': 'Suicide/ideation',
  'torture': 'Torture of MCs',
  'death': null,
  'grief': null,
  'past abuse': 'Past abuse',
  'no hea': 'No HEA',
  'cliffhanger': null,
};

/**
 * Parse a Goodreads CSV row and extract taxonomy-mapped tags
 */
export function parseGoodreadsRow(row) {
  const title = row['Title'] || '';
  const author = row['Author'] || row['Author l-f'] || '';
  const isbn13 = (row['ISBN13'] || '').replace(/[="]/g, '').trim();
  const isbn = (row['ISBN'] || '').replace(/[="]/g, '').trim();
  const rating = parseFloat(row['My Rating']) || 0;
  const avgRating = parseFloat(row['Average Rating']) || 0;
  const pages = parseInt(row['Number of Pages']) || null;
  const shelves = (row['Bookshelves'] || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const exclusiveShelf = (row['Exclusive Shelf'] || '').trim().toLowerCase();
  const dateRead = row['Date Read'] || null;

  // Map shelves to genres
  const genres = {};
  const tropes = {};
  for (const shelf of shelves) {
    const genre = GOODREADS_GENRE_MAP[shelf];
    if (genre) genres[genre] = 'present';
    const trope = GOODREADS_TROPE_MAP[shelf];
    if (trope) tropes[trope] = 'present';
  }

  const length = pagesToLength(pages);

  const autoTags = {};
  if (Object.keys(genres).length > 0) autoTags.genres = genres;
  if (Object.keys(tropes).length > 0) autoTags.tropes = tropes;
  if (length) autoTags.length = { [length]: 'present' };

  // Map exclusive shelf to read status
  const readStatus = mapReadStatus(exclusiveShelf) || 'read';

  return {
    title,
    author,
    isbn: isbn13 || isbn || null,
    rating: rating > 0 ? rating : null,
    avgRating,
    pages,
    shelves,
    exclusiveShelf,
    dateRead,
    readStatus,
    autoTags,
    source: 'goodreads-import',
  };
}

/**
 * Parse a StoryGraph CSV row and extract taxonomy-mapped tags
 */
export function parseStoryGraphRow(row) {
  const title = row['Title'] || '';
  const author = row['Authors'] || row['Author'] || '';
  const rating = parseFloat(row['Star Rating'] || row['Rating']) || 0;
  const pages = parseInt(row['Pages'] || row['Number of Pages']) || null;
  const readStatus = (row['Read Status'] || row['Status'] || '').trim();
  const tags = (row['Tags'] || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const sgGenres = (row['Genres'] || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const contentWarnings = (row['Content Warnings'] || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const format = (row['Format'] || '').trim();

  // Map genres
  const genres = {};
  for (const g of sgGenres) {
    const mapped = STORYGRAPH_GENRE_MAP[g] || GOODREADS_GENRE_MAP[g];
    if (mapped) genres[mapped] = 'present';
  }

  // Map moods/tags to genres and story elements
  const storyElements = {};
  for (const tag of tags) {
    const genreMatch = STORYGRAPH_MOOD_MAP[tag];
    if (genreMatch) genres[genreMatch] = 'present';
    const elementMatch = STORYGRAPH_MOOD_TO_ELEMENTS[tag];
    if (elementMatch) storyElements[elementMatch] = 'present';
    // Check trope matches too
    const tropeMatch = GOODREADS_TROPE_MAP[tag] || GOODREADS_TROPE_MAP[tag.replace(/\s+/g, '-')];
    if (tropeMatch) genres[tropeMatch] = 'present'; // will go in tropes below
  }

  // Map content warnings
  const cws = {};
  for (const cw of contentWarnings) {
    const mapped = STORYGRAPH_CW_MAP[cw];
    if (mapped) cws[mapped] = 'present';
  }

  const length = pagesToLength(pages);

  // Detect pace from tags
  let pace = null;
  if (tags.includes('fast-paced') || tags.includes('fast paced')) pace = 'fast';
  else if (tags.includes('medium-paced') || tags.includes('medium paced')) pace = 'medium';
  else if (tags.includes('slow-paced') || tags.includes('slow paced') || tags.includes('slow burn')) pace = 'slow';

  const autoTags = {};
  if (Object.keys(genres).length > 0) autoTags.genres = genres;
  if (Object.keys(storyElements).length > 0) autoTags.storyElements = storyElements;
  if (Object.keys(cws).length > 0) autoTags.contentWarnings = cws;
  if (length) autoTags.length = { [length]: 'present' };

  // Map tropes from tags
  const tropes = {};
  for (const tag of tags) {
    const tropeMatch = GOODREADS_TROPE_MAP[tag] || GOODREADS_TROPE_MAP[tag.replace(/\s+/g, '-')];
    if (tropeMatch) tropes[tropeMatch] = 'present';
  }
  if (Object.keys(tropes).length > 0) autoTags.tropes = tropes;

  return {
    title,
    author,
    isbn: null, // StoryGraph doesn't export ISBN
    rating: rating > 0 ? rating : null,
    pages,
    readStatus: mapReadStatus(readStatus) || 'read',
    tags,
    contentWarnings,
    pace,
    autoTags,
    source: 'storygraph-import',
  };
}

/* ─────────────────────────────────────────
   LIBRARYTHING CSV PARSING
───────────────────────────────────────── */
/**
 * Parse a LibraryThing CSV row
 */
export function parseLibraryThingRow(row) {
  const title = row['Title'] || row['Primary Title'] || '';
  const author = row['Author'] || row['Primary Author'] || '';
  const isbnRaw = row['ISBNs'] || row['ISBN'] || '';
  const isbn = isbnRaw.split(',')[0]?.replace(/[="[\]\s]/g, '').trim() || null;
  const rating = parseFloat(row['Rating'] || row['Stars']) || 0;
  const pages = parseInt(row['Number of Pages'] || row['Pages']) || null;
  const dateRead = row['Date Read'] || null;
  const tagsRaw = row['Tags'] || row['Collections'] || '';
  const tags = tagsRaw.split(/[,|]/).map(s => s.trim().toLowerCase()).filter(Boolean);
  const collections = (row['Collections'] || '').toLowerCase();

  // Map tags to genres/tropes using shared maps
  const genres = {};
  const tropes = {};
  for (const tag of tags) {
    const normalized = tag.replace(/\s+/g, '-');
    const genre = GOODREADS_GENRE_MAP[tag] || GOODREADS_GENRE_MAP[normalized];
    if (genre) genres[genre] = 'present';
    const trope = GOODREADS_TROPE_MAP[tag] || GOODREADS_TROPE_MAP[normalized];
    if (trope) tropes[trope] = 'present';
  }

  const length = pagesToLength(pages);
  const autoTags = {};
  if (Object.keys(genres).length > 0) autoTags.genres = genres;
  if (Object.keys(tropes).length > 0) autoTags.tropes = tropes;
  if (length) autoTags.length = { [length]: 'present' };

  // Detect read status from collections
  let readStatus = 'read';
  if (collections.includes('currently reading') || collections.includes('reading')) readStatus = 'reading';
  else if (collections.includes('to read') || collections.includes('wishlist')) readStatus = 'to-read';

  return {
    title, author, isbn,
    rating: rating > 0 ? rating : null,
    pages, dateRead, readStatus,
    autoTags,
    source: 'librarything-import',
  };
}

/* ─────────────────────────────────────────
   KINDLE CSV PARSING
───────────────────────────────────────── */
/**
 * Parse a Kindle export row — minimal data (title + author only)
 */
export function parseKindleRow(row) {
  const title = row['Title'] || row['title'] || '';
  const author = row['Authors'] || row['Author'] || row['authors'] || row['author'] || '';
  const asin = row['ASIN'] || row['asin'] || null;
  const purchaseDate = row['Purchase Date'] || row['purchase date'] || null;

  return {
    title, author, isbn: null, asin,
    rating: null,
    pages: null,
    dateRead: purchaseDate,
    readStatus: 'read',
    autoTags: {},
    source: 'kindle-import',
  };
}

/* ─────────────────────────────────────────
   BOOKBUB CSV PARSING
───────────────────────────────────────── */
const BOOKBUB_CATEGORY_MAP = {
  'fantasy': 'High fantasy', 'romance': null,
  'paranormal romance': 'Magic', 'dark romance': 'Dark romance',
  'science fiction': 'Science fiction / aliens', 'sci-fi': 'Science fiction / aliens',
  'mystery': 'Mystery', 'mystery & suspense': 'Suspense', 'thriller': 'Suspense',
  'horror': 'Horror', 'historical romance': null, 'contemporary romance': null,
  'young adult': 'Young adult', 'new adult': 'New adult',
};

/**
 * Parse a BookBub CSV row
 */
export function parseBookBubRow(row) {
  const title = row['Title'] || row['title'] || '';
  const author = row['Author Name'] || row['Author'] || row['author'] || '';
  const rating = parseFloat(row['Rating'] || row['rating']) || 0;
  const category = (row['Category'] || row['category'] || '').toLowerCase().trim();
  const dateAdded = row['Date Added'] || row['date added'] || null;

  const genres = {};
  const mapped = BOOKBUB_CATEGORY_MAP[category];
  if (mapped) genres[mapped] = 'present';

  const autoTags = {};
  if (Object.keys(genres).length > 0) autoTags.genres = genres;

  return {
    title, author, isbn: null,
    rating: rating > 0 ? rating : null,
    pages: null,
    dateRead: dateAdded,
    readStatus: 'read',
    autoTags,
    source: 'bookbub-import',
  };
}

/* ─────────────────────────────────────────
   LITERAL.CLUB CSV PARSING
───────────────────────────────────────── */
/**
 * Parse a Literal.club CSV row
 */
export function parseLiteralRow(row) {
  const title = row['Title'] || row['title'] || '';
  const author = row['Authors'] || row['Author'] || row['authors'] || row['author'] || '';
  const isbn = (row['ISBN13'] || row['isbn13'] || row['ISBN'] || row['isbn'] || '').replace(/[="]/g, '').trim() || null;
  const rating = parseFloat(row['Rating'] || row['rating'] || row['Star Rating']) || 0;
  const shelf = row['Shelf'] || row['shelf'] || row['Status'] || '';
  const dateRead = row['Date Finished'] || row['Date Read'] || null;
  const tagsRaw = row['Tags'] || row['tags'] || '';
  const tags = tagsRaw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

  const genres = {};
  const tropes = {};
  for (const tag of tags) {
    const normalized = tag.replace(/\s+/g, '-');
    const genre = GOODREADS_GENRE_MAP[tag] || GOODREADS_GENRE_MAP[normalized];
    if (genre) genres[genre] = 'present';
    const trope = GOODREADS_TROPE_MAP[tag] || GOODREADS_TROPE_MAP[normalized];
    if (trope) tropes[trope] = 'present';
  }

  const autoTags = {};
  if (Object.keys(genres).length > 0) autoTags.genres = genres;
  if (Object.keys(tropes).length > 0) autoTags.tropes = tropes;

  return {
    title, author, isbn,
    rating: rating > 0 ? rating : null,
    pages: null,
    dateRead,
    readStatus: mapReadStatus(shelf) || 'read',
    autoTags,
    source: 'literal-import',
  };
}

/* ─────────────────────────────────────────
   OKU CSV PARSING
───────────────────────────────────────── */
/**
 * Parse an Oku CSV row
 */
export function parseOkuRow(row) {
  const title = row['title'] || row['Title'] || '';
  const author = row['creator'] || row['Creator'] || row['Author'] || row['author'] || '';
  const isbn = (row['isbn'] || row['ISBN'] || row['isbn13'] || '').replace(/[="]/g, '').trim() || null;
  const status = row['status'] || row['Status'] || '';
  const rating = parseFloat(row['rating'] || row['Rating']) || 0;
  const dateRead = row['date_finished'] || row['Date Finished'] || null;

  return {
    title, author, isbn,
    rating: rating > 0 ? rating : null,
    pages: null,
    dateRead,
    readStatus: mapReadStatus(status?.replace(/_/g, ' ')) || 'read',
    autoTags: {},
    source: 'oku-import',
  };
}

/* ─────────────────────────────────────────
   PARSER LOOKUP
───────────────────────────────────────── */
export const PLATFORM_PARSERS = {
  goodreads: parseGoodreadsRow,
  storygraph: parseStoryGraphRow,
  librarything: parseLibraryThingRow,
  kindle: parseKindleRow,
  bookbub: parseBookBubRow,
  literal: parseLiteralRow,
  oku: parseOkuRow,
};

/**
 * Look up a book in existing database by ISBN first, then title+author fuzzy match.
 * @returns {Object|null} matching existing book or null
 */
export function findExistingBook(parsedBook, existingBooks) {
  // 1. ISBN exact match
  if (parsedBook.isbn) {
    const byIsbn = existingBooks.find(b => b.isbn === parsedBook.isbn);
    if (byIsbn) return byIsbn;
  }

  // 2. Fuzzy title+author match
  const normTitle = normalizeTitle(parsedBook.title);
  const normAuthor = normalizeAuthor(parsedBook.author);
  if (!normTitle) return null;

  let bestMatch = null;
  let bestSim = 0;

  for (const existing of existingBooks) {
    const existingAuthor = normalizeAuthor(existing.author);
    if (!existingAuthor || !normAuthor) continue;
    const authorSim = titleSimilarity(normAuthor, existingAuthor);
    if (authorSim < 0.5) continue;

    const existingTitle = normalizeTitle(existing.title);
    const sim = titleSimilarity(normTitle, existingTitle);
    if (sim > bestSim && sim >= 0.6) {
      bestSim = sim;
      bestMatch = existing;
    }
  }

  return bestMatch;
}

/**
 * Detect CSV format from column headers.
 * Returns: 'goodreads' | 'storygraph' | 'librarything' | 'kindle' | 'bookbub' | 'literal' | 'oku' | 'unknown'
 */
export function detectCsvFormat(columns) {
  const cols = columns.map(c => c.toLowerCase().trim());

  // Goodreads — has 'book id' + 'bookshelves' or 'exclusive shelf'
  if (cols.includes('book id') && cols.includes('bookshelves')) return 'goodreads';
  if (cols.includes('exclusive shelf')) return 'goodreads';

  // StoryGraph — has 'read status' + 'tags'/'genres', or 'star rating' + 'content warnings'
  if (cols.includes('read status') && (cols.includes('tags') || cols.includes('genres'))) return 'storygraph';
  if (cols.includes('star rating') && cols.includes('content warnings')) return 'storygraph';

  // LibraryThing — has 'primary title' or ('book-id' + 'lcc'), or 'entry date'
  if (cols.includes('primary title') || cols.includes('primary author')) return 'librarything';
  if (cols.includes('book-id') && (cols.includes('lcc') || cols.includes('dewey decimal'))) return 'librarything';
  if (cols.includes('entry date') && cols.includes('tags') && cols.includes('collections')) return 'librarything';

  // Kindle — has 'asin' column
  if (cols.includes('asin')) return 'kindle';
  if (cols.includes('purchase date') && cols.includes('title') && !cols.includes('rating')) return 'kindle';

  // Literal.club — has 'shelf' + 'isbn13'
  if (cols.includes('shelf') && cols.includes('isbn13')) return 'literal';
  if (cols.includes('shelf') && cols.includes('authors') && !cols.includes('bookshelves')) return 'literal';

  // Oku — has 'creator' column or 'status' with 'isbn'
  if (cols.includes('creator')) return 'oku';
  if (cols.includes('status') && cols.includes('isbn') && !cols.includes('read status')) return 'oku';

  // BookBub — has 'author name' or 'category'
  if (cols.includes('author name') && cols.includes('category')) return 'bookbub';
  if (cols.includes('author name') && cols.includes('date added')) return 'bookbub';

  return 'unknown';
}
